<script setup>
import {computed, onMounted, ref} from 'vue';
import {useI18n} from 'vue-i18n';
import router from '@/router';
import {getLoggedUser} from '@/services/AuthService';
import NotificationSettingsService from '@/services/NotificationSettingsService';
import IntermediaryService from '@/services/IntermediaryService';
import Input from '@/components/forms/Input.vue';
import Button from '@/components/forms/Button.vue';
import Multiselect from '@vueform/multiselect';

const {t} = useI18n();

// Role přihlášeného uživatele
const userRole = ref(null);
const userIntermediaryId = ref(null);

const isAdmin = computed(() => ['ADMIN', 'SUPERADMIN'].includes(userRole.value));
// Tenant nastavení může konfigurovat pouze ADMIN nebo SUPERADMIN
const canManageTenant = computed(() => isAdmin.value);

const activeTab = ref('global');

// Globální nastavení (seznam položek z API)
const globalSettings = ref([]);
// Tenantové nastavení (seznam položek z API s global_setting + tenant_setting)
const tenantSettings = ref([]);

// Formulářový stav pro globální nastavení (klíčováno notification_type)
const globalForms = ref({});
// Formulářový stav pro tenantové nastavení
const tenantForms = ref({});

// Accordion — která karta je rozbalená
const expandedGlobal = ref({});
const expandedTenant = ref({});

// Sleduje, zda je formulář předvyplněn z computed defaults (tj. v DB není žádný záznam)
const globalDefaultPrefilled = ref({});

// Pro admina — výběr intermediary, jehož tenantové nastavení chce konfigurovat
const intermediaries = ref([]);
const selectedIntermediaryId = ref(null);

// Mody pro tenantové nastavení
const modeOptions = [
    {value: 'inherit', label: t('notificationSettings.mode_inherit')},
    {value: 'extend', label: t('notificationSettings.mode_extend')},
    {value: 'override', label: t('notificationSettings.mode_override')},
    {value: 'disabled', label: t('notificationSettings.mode_disabled')},
];

// Typy, u nichž nelze nastavit kopii přímému poradci klienta —
// notifikace se odesílá bez vazby na konkrétního klienta (hromadně), takže poradce nelze odvodit.
const TYPES_WITHOUT_ADVISOR_COPY = [];

// Typy, u kterých CC poradci funguje jen při automatickém odeslání (ne při ručním
// hromadném odeslání z administrace, kde nelze konkrétního klienta/poradce odvodit).
const TYPES_WITH_PARTIAL_ADVISOR_COPY = ['documents_to_sign'];

// Možnosti příjemců pro multiselect — pro typy bez poradce se skryje volba intermediary_person
const allRecipientOptions = [
    {value: 'intermediary',             label: t('notificationSettings.cc_intermediary')},
    {value: 'intermediary_back_office', label: t('notificationSettings.cc_intermediary_back_office')},
    {value: 'intermediary_person',      label: t('notificationSettings.cc_intermediary_person')},
    {value: 'emails',                   label: t('notificationSettings.recipient_emails')},
];

function getRecipientOptions(notificationType) {
    if (TYPES_WITHOUT_ADVISOR_COPY.includes(notificationType)) {
        return allRecipientOptions.filter(o => o.value !== 'intermediary_person');
    }
    return allRecipientOptions;
}

function hasPartialAdvisorCopy(notificationType) {
    return TYPES_WITH_PARTIAL_ADVISOR_COPY.includes(notificationType);
}

/**
 * Převede API formát nastavení na interní stav formuláře.
 * API ukládá cc_intermediary (bool) + cc_intermediary_person (bool) + cc_emails (string),
 * formulář používá cc_recipients (array) + cc_emails (string).
 */
function apiToForm(setting) {
    const cc = [];
    if (setting?.cc_intermediary)             cc.push('intermediary');
    if (setting?.cc_intermediary_back_office) cc.push('intermediary_back_office');
    if (setting?.cc_intermediary_person)      cc.push('intermediary_person');
    if (setting?.cc_emails)                   cc.push('emails');

    const bcc = [];
    if (setting?.bcc_intermediary)             bcc.push('intermediary');
    if (setting?.bcc_intermediary_back_office) bcc.push('intermediary_back_office');
    if (setting?.bcc_intermediary_person)      bcc.push('intermediary_person');
    if (setting?.bcc_emails)                   bcc.push('emails');

    return {
        cc_recipients:  cc,
        cc_emails:      setting?.cc_emails  ?? '',
        bcc_recipients: bcc,
        bcc_emails:     setting?.bcc_emails ?? '',
        push_enabled:   setting?.push_enabled ?? false,
        // Mail je opt-out (na rozdíl od push) — bez záznamu v DB se e-mail posílá jako dosud
        mail_enabled:   setting?.mail_enabled ?? true,
    };
}

/**
 * Převede interní stav formuláře zpět na API formát pro odeslání.
 * Pole cc_emails se posílá jen pokud je vybrána volba 'emails'.
 */
function formToApi(form) {
    return {
        cc_intermediary:             form.cc_recipients.includes('intermediary'),
        cc_intermediary_back_office: form.cc_recipients.includes('intermediary_back_office'),
        cc_intermediary_person:      form.cc_recipients.includes('intermediary_person'),
        cc_emails:                   form.cc_recipients.includes('emails') ? form.cc_emails : '',
        bcc_intermediary:            form.bcc_recipients.includes('intermediary'),
        bcc_intermediary_back_office: form.bcc_recipients.includes('intermediary_back_office'),
        bcc_intermediary_person:     form.bcc_recipients.includes('intermediary_person'),
        bcc_emails:                  form.bcc_recipients.includes('emails') ? form.bcc_emails : '',
        push_enabled:                form.push_enabled,
        mail_enabled:                form.mail_enabled,
    };
}

onMounted(async () => {
    const user = await getLoggedUser();
    userRole.value = user.role;
    userIntermediaryId.value = user.intermediary?.id ?? null;

    if (isAdmin.value) {
        await loadGlobalSettings();
        // Načtení intermediary pro výběr v záložce Nastavení tenanta
        const result = await IntermediaryService.getAll();
        intermediaries.value = Array.isArray(result) ? result : [];
    }
});

async function loadGlobalSettings() {
    const data = await NotificationSettingsService.getAll().catch(() => []);
    globalSettings.value = data;
    data.forEach(item => {
        // Pokud není DB nastavení, předvyplní se formulář ze systémových defaults
        const isDefault = !item.setting;
        globalDefaultPrefilled.value[item.notification_type] = isDefault;
        globalForms.value[item.notification_type] = apiToForm(item.setting ?? item.defaults);
    });
}

async function loadTenantSettings() {
    const params = isAdmin.value && selectedIntermediaryId.value
        ? {intermediary_id: selectedIntermediaryId.value}
        : {};
    const data = await NotificationSettingsService.getTenantAll(params).catch(() => []);
    tenantSettings.value = data;
    data.forEach(item => {
        tenantForms.value[item.notification_type] = {
            mode: item.tenant_setting?.mode ?? 'inherit',
            ...apiToForm(item.tenant_setting),
        };
    });
}

async function saveGlobal(notificationType) {
    await NotificationSettingsService.saveGlobal(notificationType, formToApi(globalForms.value[notificationType]));
    await loadGlobalSettings();
}

async function saveTenant(notificationType) {
    const data = {
        ...formToApi(tenantForms.value[notificationType]),
        mode: tenantForms.value[notificationType].mode,
        ...(selectedIntermediaryId.value ? {intermediary_id: selectedIntermediaryId.value} : {}),
    };
    await NotificationSettingsService.saveTenant(notificationType, data);
    await loadTenantSettings();
}

async function onIntermediaryChange() {
    tenantSettings.value = [];
    tenantForms.value = {};
    expandedTenant.value = {};
    if (selectedIntermediaryId.value) {
        await loadTenantSettings();
    }
}

// Vlastní CC/BCC pole jsou relevantní jen pro mode extend a override
function modeNeedsOwnSettings(mode) {
    return ['extend', 'override'].includes(mode);
}

// Vrátí true pokud má notifikace alespoň jedno aktivní systémové výchozí nastavení (CC/BCC)
function hasSystemDefaults(item) {
    return item.defaults?.cc_intermediary_person || item.defaults?.cc_intermediary;
}

function toggleGlobal(type) {
    expandedGlobal.value[type] = !expandedGlobal.value[type];
}

function toggleTenant(type) {
    expandedTenant.value[type] = !expandedTenant.value[type];
}
</script>

<template>
    <div class="page-section">
        <div class="page-section__heading">
            <a @click="router.back()" class="page-section__heading__back">
                {{ t('settings.back') }}
            </a>
            <div class="page-section__title">{{ t('notificationSettings.title') }}</div>
        </div>

        <div class="page-section__content">

            <!-- Záložky — globální nastavení jen pro admina -->
            <ul class="nav nav-tabs mb-4" v-if="isAdmin && canManageTenant">
                <li class="nav-item">
                    <a class="nav-link"
                       :class="{active: activeTab === 'global', 'tab-active': activeTab === 'global', 'tab-inactive': activeTab !== 'global'}"
                       style="cursor:pointer"
                       @click="activeTab = 'global'">
                        {{ t('notificationSettings.global_settings') }}
                    </a>
                </li>
                <li class="nav-item">
                    <a class="nav-link"
                       :class="{active: activeTab === 'tenant', 'tab-active': activeTab === 'tenant', 'tab-inactive': activeTab !== 'tenant'}"
                       style="cursor:pointer"
                       @click="activeTab = 'tenant'">
                        {{ t('notificationSettings.tenant_settings') }}
                    </a>
                </li>
            </ul>

            <!-- ==================== GLOBÁLNÍ NASTAVENÍ ==================== -->
            <div v-if="activeTab === 'global' && isAdmin">
                <p class="text-muted mb-3">{{ t('notificationSettings.global_description') }}</p>

                <div v-for="item in globalSettings" :key="item.notification_type" class="card mb-3">
                    <!-- Hlavička: název vlevo, vpravo: push badge → status badge → chevron -->
                    <div class="card-header d-flex justify-content-between align-items-center mb-3"
                         style="cursor:pointer"
                         @click="toggleGlobal(item.notification_type)">
                        <div>
                            <span class="fw-semibold">{{ t('notificationSettings.types.' + item.notification_type) }}</span>
                            <div class="text-muted small mt-1">
                                {{ t('notificationSettings.to') }}: {{ t('notificationSettings.to_recipient.' + item.to_recipient) }}
                            </div>
                        </div>
                        <div class="d-flex align-items-center gap-2">
                            <!-- Push badge — zobrazuje aktuální stav push; skryto pokud typ push nepodporuje -->
                            <span v-if="item.has_push !== false" class="badge"
                                  :class="globalForms[item.notification_type].push_enabled ? 'bg-primary' : 'bg-light text-dark border'">
                                <i class="mdi mdi-bell me-1"/>{{ t('notificationSettings.push_enabled') }}
                            </span>
                            <!-- Mail badge — zobrazuje aktuální stav e-mailu -->
                            <span class="badge"
                                  :class="globalForms[item.notification_type].mail_enabled ? 'bg-primary' : 'bg-light text-dark border'">
                                <i class="mdi mdi-email-outline me-1"/>{{ t('notificationSettings.mail_enabled') }}
                            </span>
                            <!-- Status badge (konfigurováno / výchozí chování) + chevron pro rozbalení -->
                            <span v-if="item.setting" class="badge bg-success">{{ t('notificationSettings.configured') }}</span>
                            <span v-else class="badge bg-secondary">{{ t('notificationSettings.using_default') }}</span>
                            <i class="mdi" :class="expandedGlobal[item.notification_type] ? 'mdi-chevron-up' : 'mdi-chevron-down'"></i>
                        </div>
                    </div>

                    <!-- Tělo: push toggle + CC/BCC nastavení -->
                    <div v-if="expandedGlobal[item.notification_type]" class="card-body">

                        <!-- Info: formulář předvyplněn z computed defaults, v DB zatím není záznam -->
                        <div v-if="globalDefaultPrefilled[item.notification_type]" class="alert alert-info mb-3 small">
                            <i class="mdi mdi-information-outline me-1"/>{{ t('notificationSettings.prefilled_from_defaults') }}
                        </div>

                        <!-- Upozornění: CC poradci funguje jen při automatickém odeslání, ne při ručním hromadném -->
                        <div v-if="hasPartialAdvisorCopy(item.notification_type)" class="alert alert-warning mb-3 small">
                            <i class="mdi mdi-alert-outline me-1"/>{{ t('notificationSettings.partial_advisor_copy_warning') }}
                        </div>

                        <!-- Push notifikace toggle — pouze pro typy, které push podporují -->
                        <div v-if="item.has_push !== false" class="form-check form-switch mb-3">
                            <input class="form-check-input" type="checkbox" role="switch"
                                   :id="'push_' + item.notification_type"
                                   v-model="globalForms[item.notification_type].push_enabled"/>
                            <label class="form-check-label" :for="'push_' + item.notification_type">
                                <i class="mdi mdi-bell me-1"/>{{ t('notificationSettings.push_enabled') }}
                            </label>
                        </div>

                        <!-- Mail notifikace toggle — vypnutí přebije výchozí (systémové) chování -->
                        <div class="form-check form-switch mb-3">
                            <input class="form-check-input" type="checkbox" role="switch"
                                   :id="'mail_' + item.notification_type"
                                   v-model="globalForms[item.notification_type].mail_enabled"/>
                            <label class="form-check-label" :for="'mail_' + item.notification_type">
                                <i class="mdi mdi-email-outline me-1"/>{{ t('notificationSettings.mail_enabled') }}
                            </label>
                        </div>

                        <div class="row justify-content-center">
                            <!-- CC sekce -->
                            <div class="col-md-6">
                                <h6 class="mb-2">{{ t('notificationSettings.cc') }}</h6>
                                <Multiselect
                                    v-model="globalForms[item.notification_type].cc_recipients"
                                    :options="getRecipientOptions(item.notification_type)"
                                    value-prop="value"
                                    label="label"
                                    :close-on-select="false"
                                    mode="tags"
                                    :placeholder="t('notificationSettings.recipients_placeholder')"
                                />
                                <div v-if="globalForms[item.notification_type].cc_recipients.includes('emails')" class="mt-3">
                                    <label class="form-label small">{{ t('notificationSettings.cc_emails') }}</label>
                                    <input type="text" class="form-control"
                                           v-model="globalForms[item.notification_type].cc_emails"
                                           :placeholder="t('notificationSettings.emails_placeholder')"/>
                                </div>
                            </div>

                            <!-- BCC sekce -->
                            <div class="col-md-6">
                                <h6 class="mb-2">{{ t('notificationSettings.bcc') }}</h6>
                                <Multiselect
                                    v-model="globalForms[item.notification_type].bcc_recipients"
                                    :options="getRecipientOptions(item.notification_type)"
                                    value-prop="value"
                                    label="label"
                                    :close-on-select="false"
                                    mode="tags"
                                    :placeholder="t('notificationSettings.recipients_placeholder')"
                                />
                                <div v-if="globalForms[item.notification_type].bcc_recipients.includes('emails')" class="mt-3">
                                    <label class="form-label small">{{ t('notificationSettings.bcc_emails') }}</label>
                                    <input type="text" class="form-control"
                                           v-model="globalForms[item.notification_type].bcc_emails"
                                           :placeholder="t('notificationSettings.emails_placeholder')"/>
                                </div>
                            </div>
                        </div>

                        <div class="d-flex justify-content-end mt-3">
                            <Button :label="t('notificationSettings.save')"
                                    @click="saveGlobal(item.notification_type)"/>
                        </div>
                    </div>
                </div>
            </div>

            <!-- ==================== TENANTOVÉ NASTAVENÍ ==================== -->
            <div v-if="activeTab === 'tenant' && canManageTenant">
                <p class="text-muted mb-3">{{ t('notificationSettings.tenant_description') }}</p>

                <!-- Výběr intermediary — pouze pro admina -->
                <div v-if="isAdmin" class="card mb-4">
                    <div class="card-body">
                        <label class="form-label fw-semibold">{{ t('notificationSettings.select_intermediary') }}</label>
                        <select class="form-select" v-model="selectedIntermediaryId" @change="onIntermediaryChange">
                            <option :value="null" disabled>{{ t('notificationSettings.select_intermediary_placeholder') }}</option>
                            <option v-for="intermediary in intermediaries" :key="intermediary.id" :value="intermediary.id">
                                {{ intermediary.intermediary_name || intermediary.name || intermediary.id }}
                            </option>
                        </select>
                    </div>
                </div>

                <!-- Zpráva, pokud admin nevybral intermediary -->
                <div v-if="isAdmin && !selectedIntermediaryId" class="alert alert-info">
                    {{ t('notificationSettings.select_intermediary_first') }}
                </div>

                <!-- Seznam notifikací pro vybraného tenanta -->
                <template v-if="tenantSettings.length > 0">
                    <div v-for="item in tenantSettings" :key="item.notification_type" class="card mb-3">
                        <!-- Hlavička: název vlevo, vpravo: push badge → mode badge → globální badge → chevron -->
                        <div class="card-header d-flex justify-content-between align-items-center mb-3"
                             style="cursor:pointer"
                             @click="toggleTenant(item.notification_type)">
                            <div>
                                <span class="fw-semibold">{{ t('notificationSettings.types.' + item.notification_type) }}</span>
                                <div class="text-muted small mt-1">
                                    {{ t('notificationSettings.to') }}: {{ t('notificationSettings.to_recipient.' + item.to_recipient) }}
                                </div>
                            </div>
                            <div class="d-flex align-items-center gap-2">
                                <!-- Push badge — skryto pokud typ push nepodporuje -->
                                <span v-if="item.has_push !== false" class="badge"
                                      :class="tenantForms[item.notification_type].push_enabled ? 'bg-primary' : 'bg-light text-dark border'">
                                    <i class="mdi mdi-bell me-1"/>{{ t('notificationSettings.push_enabled') }}
                                </span>
                                <!-- Mail badge -->
                                <span class="badge"
                                      :class="tenantForms[item.notification_type].mail_enabled ? 'bg-primary' : 'bg-light text-dark border'">
                                    <i class="mdi mdi-email-outline me-1"/>{{ t('notificationSettings.mail_enabled') }}
                                </span>
                                <span v-if="item.tenant_setting" class="badge bg-primary">
                                    {{ modeOptions.find(m => m.value === item.tenant_setting.mode)?.label }}
                                </span>
                                <span v-else class="badge bg-secondary">{{ t('notificationSettings.mode_inherit') }}</span>
                                <span v-if="item.global_setting" class="badge bg-light text-dark border" :title="t('notificationSettings.global_configured')">
                                    {{ t('notificationSettings.global_badge') }}
                                </span>
                                <i class="mdi" :class="expandedTenant[item.notification_type] ? 'mdi-chevron-up' : 'mdi-chevron-down'"></i>
                            </div>
                        </div>

                        <!-- Tělo formuláře -->
                        <div v-if="expandedTenant[item.notification_type]" class="card-body">

                            <!-- Přehled globálního nastavení (readonly) -->
                            <div v-if="item.global_setting" class="alert alert-light border mb-3 small">
                                <strong>{{ t('notificationSettings.global_setting_preview') }}</strong>
                                <span class="ms-2">
                                    CC: {{ [item.global_setting.cc_intermediary ? t('notificationSettings.cc_intermediary') : null,
                                            item.global_setting.cc_intermediary_person ? t('notificationSettings.cc_intermediary_person') : null,
                                            item.global_setting.cc_emails || null].filter(Boolean).join(', ') || '—' }}
                                </span>
                                <span class="ms-3">
                                    BCC: {{ [item.global_setting.bcc_intermediary ? t('notificationSettings.bcc_intermediary') : null,
                                             item.global_setting.bcc_intermediary_person ? t('notificationSettings.bcc_intermediary_person') : null,
                                             item.global_setting.bcc_emails || null].filter(Boolean).join(', ') || '—' }}
                                </span>
                                <!-- Push stav z globálního nastavení — jen pokud typ push podporuje -->
                                <span v-if="item.has_push !== false" class="ms-3">
                                    <i class="mdi mdi-bell me-1"/>{{ item.global_setting.push_enabled ? t('notificationSettings.push_enabled') : t('notificationSettings.push_disabled') }}
                                </span>
                                <!-- Mail stav z globálního nastavení -->
                                <span class="ms-3">
                                    <i class="mdi mdi-email-outline me-1"/>{{ item.global_setting.mail_enabled ? t('notificationSettings.mail_enabled') : t('notificationSettings.mail_disabled') }}
                                </span>
                            </div>
                            <div v-else class="alert alert-light border mb-3 small">
                                {{ t('notificationSettings.global_no_setting') }}
                            </div>

                            <!-- Upozornění: CC poradci funguje jen při automatickém odeslání, ne při ručním hromadném -->
                            <div v-if="hasPartialAdvisorCopy(item.notification_type)" class="alert alert-warning mb-3 small">
                                <i class="mdi mdi-alert-outline me-1"/>{{ t('notificationSettings.partial_advisor_copy_warning') }}
                            </div>

                            <!-- Systémové výchozí CC/BCC — read-only, nelze vypnout na úrovni tenanta -->
                            <div v-if="hasSystemDefaults(item)" class="alert alert-secondary border mb-3 small">
                                <strong>{{ t('notificationSettings.system_defaults') }}</strong>
                                <div class="mt-1">
                                    <span v-if="item.defaults.cc_intermediary_person" class="me-3">
                                        <i class="mdi mdi-account me-1"/>CC: {{ t('notificationSettings.cc_intermediary_person') }}
                                    </span>
                                    <span v-if="item.defaults.cc_intermediary">
                                        <i class="mdi mdi-domain me-1"/>CC: {{ t('notificationSettings.cc_intermediary') }}
                                    </span>
                                </div>
                                <div class="mt-2 fst-italic text-muted">{{ t('notificationSettings.system_defaults_description') }}</div>
                            </div>

                            <!-- Push notifikace toggle — pouze pro typy, které push podporují -->
                            <div v-if="item.has_push !== false" class="form-check form-switch mb-3">
                                <input class="form-check-input" type="checkbox" role="switch"
                                       :id="'tenant_push_' + item.notification_type"
                                       v-model="tenantForms[item.notification_type].push_enabled"/>
                                <label class="form-check-label" :for="'tenant_push_' + item.notification_type">
                                    <i class="mdi mdi-bell me-1"/>{{ t('notificationSettings.push_enabled') }}
                                </label>
                            </div>

                            <!-- Mail notifikace toggle — relevantní jen pro extend/override (viz mode select níže) -->
                            <div class="form-check form-switch mb-3">
                                <input class="form-check-input" type="checkbox" role="switch"
                                       :id="'tenant_mail_' + item.notification_type"
                                       v-model="tenantForms[item.notification_type].mail_enabled"/>
                                <label class="form-check-label" :for="'tenant_mail_' + item.notification_type">
                                    <i class="mdi mdi-email-outline me-1"/>{{ t('notificationSettings.mail_enabled') }}
                                </label>
                            </div>

                            <!-- Výběr módu -->
                            <div class="mb-3">
                                <label class="form-label fw-semibold">{{ t('notificationSettings.mode') }}</label>
                                <select class="form-select" v-model="tenantForms[item.notification_type].mode">
                                    <option v-for="option in modeOptions" :key="option.value" :value="option.value">
                                        {{ option.label }}
                                    </option>
                                </select>
                            </div>

                            <!-- Vlastní CC/BCC — zobrazit jen pro extend a override -->
                            <template v-if="modeNeedsOwnSettings(tenantForms[item.notification_type].mode)">
                                <div class="row">
                                    <!-- CC sekce -->
                                    <div class="col-md-6">
                                        <h6 class="mb-2">{{ t('notificationSettings.cc') }}</h6>
                                        <Multiselect
                                            v-model="tenantForms[item.notification_type].cc_recipients"
                                            :options="getRecipientOptions(item.notification_type)"
                                            value-prop="value"
                                            label="label"
                                            :close-on-select="false"
                                            mode="tags"
                                            :placeholder="t('notificationSettings.recipients_placeholder')"
                                        />
                                        <div v-if="tenantForms[item.notification_type].cc_recipients.includes('emails')" class="mt-3">
                                            <label class="form-label small">{{ t('notificationSettings.cc_emails') }}</label>
                                            <input type="text" class="form-control"
                                                   v-model="tenantForms[item.notification_type].cc_emails"
                                                   :placeholder="t('notificationSettings.emails_placeholder')"/>
                                        </div>
                                    </div>

                                    <!-- BCC sekce -->
                                    <div class="col-md-6">
                                        <h6 class="mb-2">{{ t('notificationSettings.bcc') }}</h6>
                                        <Multiselect
                                            v-model="tenantForms[item.notification_type].bcc_recipients"
                                            :options="getRecipientOptions(item.notification_type)"
                                            value-prop="value"
                                            label="label"
                                            :close-on-select="false"
                                            mode="tags"
                                            :placeholder="t('notificationSettings.recipients_placeholder')"
                                        />
                                        <div v-if="tenantForms[item.notification_type].bcc_recipients.includes('emails')" class="mt-3">
                                            <label class="form-label small">{{ t('notificationSettings.bcc_emails') }}</label>
                                            <input type="text" class="form-control"
                                                   v-model="tenantForms[item.notification_type].bcc_emails"
                                                   :placeholder="t('notificationSettings.emails_placeholder')"/>
                                        </div>
                                    </div>
                                </div>
                            </template>

                            <div class="d-flex justify-content-end mt-2">
                                <Button :label="t('notificationSettings.save')"
                                        @click="saveTenant(item.notification_type)"/>
                            </div>
                        </div>
                    </div>
                </template>
            </div>

        </div>
    </div>
</template>

<style scoped>
/* Bootstrap .nav-link má vlastní barvy — přepíšeme !important pomocí --primaryColor tenanta */
.nav-link.tab-active {
    color: var(--primaryColor) !important;
    font-weight: 600;
}
.nav-link.tab-inactive {
    color: #000 !important;
    font-weight: normal;
}

/* Multiselect dropdown se musí zobrazit přes okraj karty — Bootstrap .card jinak dropdown ořízne */
.card, .card-body {
    overflow: visible;
}
</style>